<template>
  <v-container>
    <v-row class="pl-3 pt-3">
      <v-col cols="12" class="pt-0">
        <h3 class="text-h6 font-weight-bold mb-6 d-flex">
          <span>{{ loan.id ? 'Edit' : 'New' }} Application</span>
        </h3>

        <!-- Loan details form -->
        <ValidationObserver ref="loanObserver">
          <v-row dense justify="center">
            <v-col cols="12" lg="12">
              <ValidationProvider
                name="Loan Product"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  outlined
                  label="Loan Product"
                  dense
                  :disabled="!!loan?.id"
                  background-color="white"
                  :error-messages="errors"
                  :items="loanProducts"
                  @change="onTermsChanged('product')"
                  item-value="id"
                  item-text="loanProductName"
                  v-model="loan.loanProductDetails.id"
                >
                  <template v-slot:label>
                    Loan Product
                    <span class="red--text">*</span>
                  </template>
                </v-autocomplete>
              </ValidationProvider>
            </v-col>
            <v-col v-if="motorVehicleType" cols="12" lg="12">
              <ValidationProvider
                name="Vehicle Age"
                :rules="`required|min_value:0|max_value:${
                  loan.loanProductDetails?.maximumAge - 1 || 0
                }`"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  outlined
                  min="0"
                  type="number"
                  label="Vehicle Age"
                  dense
                  @change="onTermsChanged('age')"
                  background-color="white"
                  :error-messages="errors"
                  v-model="loan.vehicleAge"
                >
                  <template v-slot:label>
                    Vehicle Age
                    <span class="red--text">*</span>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                name="Select Policy"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  outlined
                  label="Select Policy"
                  dense
                  @change="onTermsChanged('policy')"
                  :error-messages="errors"
                  :items="policyCategories"
                  v-model="loan.loanCategory"
                  background-color="white"
                >
                  <template v-slot:label>
                    Select Policy
                    <span class="red--text">*</span>
                  </template>
                </v-autocomplete>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                name="Internal Amount"
                :rules="`required|min_value:${
                  loan?.originalTerms?.loanFloor || 0
                }|max_value:999999999999`"
                v-slot="{ errors }"
              >
                <vuetify-money
                  type="number"
                  outlined
                  dense
                  required
                  label="Total Internal Loan Amount"
                  v-model="loan.totalInternalLoanAmount"
                  :disabled="isWaiverApproved('loanAmount')"
                  @input="onTermsChanged('amount')"
                  :error-messages="errors"
                  :options="VuetifyMoneyOptions"
                  :hint="`Member can get up to ${formatToCurrency(
                    loan?.maxTerms?.loanAmount
                  )}`"
                  persistent-hint
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                name="Payment Frequency"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-select
                  outlined
                  label="Payment Frequency"
                  :items="paymentFrequencies"
                  item-value="id"
                  item-text="name"
                  dense
                  required
                  background-color="white"
                  :error-messages="errors"
                  v-model="loan.frequencyName"
                  @change="onTermsChanged('frequency')"
                  :disabled="isWaiverApproved('frequencyTenure')"
                >
                  <template v-slot:label>
                    Payment Frequency
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" lg="12">
              <ValidationProvider
                name="Terms/Tenure"
                :rules="`required|min_value:1|max_value:3000`"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  outlined
                  min="1"
                  type="number"
                  label="Terms/Tenure"
                  dense
                  :disabled="isWaiverApproved('frequencyTenure')"
                  background-color="white"
                  :error-messages="errors"
                  v-model="loan.frequencyTenure"
                  @change="onTermsChanged('tenure')"
                  :hint="`Member can get up to ${
                    loan?.maxTerms?.frequencyTenure
                  } ${
                    !loan.frequencyNoun ? '-' : loan.frequencyNoun + '(s)'
                  } to repay`"
                  persistent-hint
                >
                  <template v-slot:label>
                    Terms/Tenure
                    <span class="red--text">*</span>
                  </template>
                  <span slot="append" style="font-size: 1em" class="pt-1">{{
                    !loan.frequencyNoun ? 'N/A' : loan.frequencyNoun + '(s)'
                  }}</span>
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                name="Payment Method"
                rules="required"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-select
                  outlined
                  label="Payment Method"
                  dense
                  :items="paymentMethods"
                  item-value="subRiskFactorName"
                  item-text="subRiskFactorName"
                  background-color="white"
                  :error-messages="errors"
                  v-model="loan.paymentMethod"
                  @change="onTermsChanged('payment')"
                >
                  <template v-slot:label>
                    Payment Method
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>
            </v-col>
            <v-col class="py-0" cols="12" lg="6">
              <ValidationProvider
                name="Shares"
                :rules="`required|min_value:0|max_value:100`"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  outlined
                  min="1"
                  type="number"
                  label="Shares"
                  dense
                  background-color="white"
                  :error-messages="errors"
                  v-model="loan.shares"
                  @change="onTermsChanged('shares')"
                  :disabled="isWaiverApproved('shares')"
                >
                  <template v-slot:label>
                    Shares
                    <span class="red--text">*</span>
                  </template>
                  <span slot="append" style="font-size: 1em" class="pt-1"
                    >%</span
                  >
                </v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col class="py-0" cols="12" lg="6">
              <ValidationProvider
                name="LTV"
                :rules="`required|min_value:0`"
                events="['blur']"
                v-slot="{ errors }"
              >
                <v-text-field
                  outlined
                  min="1"
                  type="number"
                  label="LTV"
                  dense
                  background-color="white"
                  :error-messages="errors"
                  v-model="loan.valueCollateralUsed"
                  @change="onTermsChanged('ltv')"
                  :disabled="isWaiverApproved('valueCollateralUsed')"
                >
                  <template v-slot:label>
                    LTV
                    <span class="red--text">*</span>
                  </template>
                  <span slot="append" style="font-size: 1em" class="pt-1"
                    >%</span
                  >
                </v-text-field>
              </ValidationProvider>
            </v-col>

            <!-- Other Options -->
            <!--<v-col cols="12" class="py-0">
              <v-expansion-panels accordion style="min-height: 12px" flat>
                <v-expansion-panel class="transparent">
                  <v-expansion-panel-header class="pa-0 my-0 transparent">
                    <a href="#">Other Options</a>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="mx-2 mb-2">
                    <v-checkbox
                      color="info"
                      class="mt-0"
                      label="Sample Loan"
                      v-model="loan.isTest"
                      id="sampleCheckbox"
                      hint="This SHOULD only be selected for testing purposes."
                      :persistent-hint="true"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col> -->

            <!-- Loan Terms Display -->
            <v-col>
              <h3 class="font-weight-bold light-blue--text text--darken-4">
                Loan Terms
              </h3>
              <v-divider class="mb-3" />
              <div
                class="d-flex justify-space-between light-blue--text text--darken-4 font-weight-medium"
              >
                <span>Total Loan Amount</span>
                <span>{{ asMoney(totalLoanAmount || 0) }}</span>
              </div>
              <div
                class="text-muted pl-3 d-flex justify-space-between light-blue--text text--darken-3"
              >
                <span>Total Internal Amount</span>
                <span>{{ asMoney(loan.totalInternalLoanAmount || 0) }}</span>
              </div>
              <div
                class="pl-3 d-flex justify-space-between light-blue--text text--darken-3"
              >
                <span>Total External Amount</span>
                <span>{{ asMoney(loan.totalExternalLoanAmount || 0) }}</span>
              </div>
              <div
                class="pl-3 d-flex justify-space-between light-blue--text text--darken-3"
              >
                <span>Total Consolidation/Top up</span>
                <span>{{ asMoney(loan.totalConsolidatedAmount || 0) }}</span>
              </div>
              <div
                class="pl-3 d-flex justify-space-between light-blue--text text--darken-3"
              >
                <span>Total Roll Up</span>
                <span>{{ asMoney(loan.totalRollUp || 0) }}</span>
              </div>
              <div
                class="d-flex justify-space-between mt-2 light-blue--text text--darken-4 font-weight-medium"
              >
                <span>Annual Interest</span>
              </div>
              <div
                class="pl-3 d-flex justify-space-between light-blue--text text--darken-3"
              >
                <span>Internal Interest</span>
                <span>{{ asPercent(loan.interest || 0) }}</span>
              </div>
              <div
                class="pl-3 d-flex justify-space-between light-blue--text text--darken-3"
              >
                <span>External Interest</span>
                <span>{{ totalExternalInterest }}</span>
              </div>
              <div
                class="d-flex justify-space-between light-blue--text text--darken-4 font-weight-medium"
              >
                <span>Tenor</span>
                <span
                  >{{ loan.frequencyTenure || 0 }}
                  {{ loan.frequencyNoun }} (s)</span
                >
              </div>
              <div
                class="d-flex justify-space-between light-blue--text text--darken-4 font-weight-medium"
              >
                <span>Repayment Amount</span>
                <span
                  >{{ asMoney(loan.frequencyPayment || 0) }}/{{
                    loan.frequencyNoun
                  }}</span
                >
              </div>
              <div
                class="d-flex justify-space-between light-blue--text text--darken-4 font-weight-medium"
              >
                <span>Shares</span>
                <span>{{ asPercent(loan.shares || 0) }}</span>
              </div>
              <div
                class="d-flex justify-space-between light-blue--text text--darken-4 font-weight-medium"
              >
                <span>LTV</span>
                <span>{{ asPercent(loan.valueCollateralUsed || 0) }}</span>
              </div>
            </v-col>
          </v-row>
        </ValidationObserver>
      </v-col>
      <v-col> </v-col>
    </v-row>
  </v-container>
</template>

<style>
#sampleCheckbox >>> v-expansion-panel--active > .v-expansion-panel-header {
  min-height: 24px;
}
</style>

<script lang="js">
import { mapGetters } from 'vuex';
import PageMixin from '@/mixins/page.mixin';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import PaymentCalculationService from '@/modules/loanpro/services/payment-calculation.service';
import LoanManagementService from '@/modules/loanpro/services/loan-management.service';
import { formatToCurrency } from '@/lib/helpers';

export default {
  mixins: [PageMixin],
  props: {

    /**
     * The loan object
     */
    loanDetails: {
      type: Object,
      default: () => ({
        loanTerms: {},
        loanPurposes: [],
        loanProductDetails: {
          maximumAge: 1,
        },
      }),
    }

  },

  watch: {

    /**
     * Watch loanDetails for change and update local loan variable
     */
    loanDetails: {
      deep: true,
      immediate: true,
      handler: function (val) {
        this.loan = structuredClone(val);
      },
    }

  },

  computed: {
    ...mapGetters("loanSettingsModule", [
      'loanProducts',
      'branches',
      'paymentFrequencies',
      'riskFactors',
    ]),




    /**
     * Get the total external interest on the loan
     */
    totalExternalInterest: function() {
      const { loanDistributions } = this.loan;
      if(!loanDistributions) return 0;
      const externalDistribution = loanDistributions
        .filter(el => el.distributionType === 'external');

      const totalWeight = externalDistribution.reduce((acc, cur) => acc += cur.loanAmount, 0);
      const weightedSum = externalDistribution.reduce((acc, cur) => acc += cur.loanAmount * cur.interest, 0)

      return this.asPercent(weightedSum/totalWeight);
    },

    /**
     * Get the list of activated payment methods in the system
     */
    paymentMethods: function () {
      const factor = this.riskFactors.find(el => el.riskFactorName == "Payment Method");
      return factor?.subRiskFactors || [];
    },

    /**
     * Get the list of policy categories
     */
    policyCategories: function () {
      return this.loanProducts?.
        find(el => el.id === this.loan?.loanProductDetails.id)
        ?.productPolicies?.map(el => el.type) || [];
    },

    /**
     * Return true if product is motor vehicle type
     */
    motorVehicleType: function () {
      return this.loan.loanProductDetails?.loanTypeDetails?.loanTypeCategory === 'Motor Vehicle';
    },

    /**
     * Get the total loan amount
     */
    totalLoanAmount: function () {
      const amount = +(this.loan.totalInternalLoanAmount || 0) +
        +(this.loan.totalExternalLoanAmount || 0) +
        +(this.loan.totalConsolidatedAmount || 0) +
        +(this.loan.totalRollUp || 0);
      return amount
    },
  },

  data() {
    return {
      loan: {
        loanTerms: {},
        loanPurposes: [],
        loanProductDetails: {
          maximumAge: 1,
        },
      },
      formatToCurrency,
      VuetifyMoneyOptions: VuetifyMoneyOptions,
      /** @type {import('vuetify').DataTableHeader[]} */
      headers: [
        { text: 'Selected', value: 'selected', sortable: false },
        { text: 'Product', value: 'product' },
        { text: 'Original Amount', value: 'amount' },
        { text: 'Current Balance', value: 'balance' },
        { text: 'Interest', value: 'interest' },
        { text: 'Shares', value: 'shares' },
        { text: 'Payment Frequency', value: 'frequency' },
        { text: 'Tenure Remaining', value: 'tenure' }
      ]
    };
  },

  methods: {
    /**
     * Return true if the loan progress is waiver approved
     */
    isWaiverApproved: function (termKey) {
      return this.loan?.loanProgress == "waiver approved" && !!this.loan?.previousTermWaiver[termKey];
    },

    /**
     * Gets call when the loan terms changed
     */
    onTermsChanged(term) {
     this.calculateTerms(term);
    },

    /**
     * validate the form
     */
    async validate() {
      const valid = await this.$refs.loanObserver.validate();
      if(!valid) {
        return { valid, message: "The loan application section is invalid and require your attention." }
      }

      return { valid,}
    },

    /**
     * Calculate the terms of the loan
     *
     * @param { Object } term - the terms of the loan
     */
    calculateTerms(term) {
      this.setFrequencyProperties();
      this.setLoanProductDetails();
      this.$emit("updated", {
        term,
        loan: this.loan
      })
    },

    /**
     * Set the loan product details based on the selected loan product
     */
    setLoanProductDetails() {
      const product = this.getLoanProduct(this.loan.loanProductDetails.id);

      this.loan.loanProductDetails = {
        ...this.loan.loanProductDetails,
        ...product
      }

      this.loan.systemCheckListItems = this.loan.id ? this.loan.systemCheckListItems || product.checklist : product.checklist;

      const loanManagementService = new LoanManagementService();
      const policy = loanManagementService.getProductPolicy(this.loan);

      if (policy) {
        loanManagementService.setPolicyTerms(this.loan, policy);
        this.$forceUpdate();
      }
    },

    /**
     * Get loan product details based on the loan product id, if a loan product is
     * already on the loan it uses that instead
     *
     * @param { String } productId - The loan product id
     * @return { Object } loan product
     */
    getLoanProduct(productId) {
       return !this.loan.id ? this.loanProducts.find(el => el.id == productId) || this.loan.loanProductDetails : this.loan.loanProductDetails;
    },

    /**
     * Set frequency noun, period, display interest and first payment days based on the selected frequency
     */
    setFrequencyProperties() {
      const defaultFrequency = this.paymentFrequencies.find(el => el.default)
      let frequency = this.paymentFrequencies.find(el => el.name == this.loan.frequencyName )
      if(!frequency && !defaultFrequency) return;

      if(!frequency && defaultFrequency) {
        frequency = defaultFrequency
        this.loan.frequencyName = frequency.name;
      }

      this.loan.frequencyNoun = frequency.noun;
      this.loan.frequencyPeriod = +frequency.period;
      this.loan.frequencyFirstPaymentDays = +frequency.frequencyFirstPaymentDays;
      this.loan.frequencyDisplayInterest = frequency.frequencyDisplayInterest;
      this.loan.frequencyTenure = +this.loan.frequencyTenure

      //store monthly tenure
      this.loan.tenure = (this.loan.frequencyTenure * 12) / this.loan.frequencyPeriod
      this.loan.internalFrequencyPayment = this.calculateInternalPayment();
      this.loan.frequencyPayment = +(this.loan.frequencyPayment ?? this.loan.internalFrequencyPayment);
    },

    /**
     * Calculate internal repayment amount
     *
     * @return { Number } calculated repayment
     */
    calculateInternalPayment() {
      const { totalInternalLoanAmount, frequencyPeriod, interest, tenure } = this.loan;

      const term = PaymentCalculationService.getFrequencyTerm(frequencyPeriod, tenure);

      const frequencyInterest = (interest/100 || 0) / frequencyPeriod;
      const { payment } = PaymentCalculationService.calculateRePaymentAmount(
        +totalInternalLoanAmount || 0,
        term,
        frequencyInterest,
        this.loan.loanProductDetails.monthlyPaymentCalculation,
        this.loan.loanProductDetails.loanProductSubType,
      );

      return payment;
    },

    /**
     * Calculate external repayment amount
     *
     * @return { Number } calculated repayment
     */
    calculateExternalPayment() {
      if(!this.loan.externalTerms) return 0;

      const { frequencyPeriod, tenure } = this.loan;
      const { interest } = this.loan.externalTerms;

      const term = PaymentCalculationService.getFrequencyTerm(frequencyPeriod, tenure);

      const frequencyInterest = (interest/100) / frequencyPeriod;

      const { payment } = PaymentCalculationService.calculateRePaymentAmount(
        +this.loan.totalExternalLoanAmount || 0,
        term,
        frequencyInterest,
        this.loan.loanProductDetails.monthlyPaymentCalculation,
        this.loan.loanProductDetails.loanProductSubType,
      );

      return payment;
    },

  },

};
</script>
